import {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  MouseEvent as ReactMouseEvent,
} from 'react';

export default function useHorizontalScroll<T extends HTMLElement>(): {
  ref: RefObject<T>;
  props: {
    onMouseDown: (e: ReactMouseEvent<T>) => void;
    onMouseMove: (e: ReactMouseEvent<T>) => void;
    onMouseUp: (e: ReactMouseEvent<T>) => void;
  };
} {
  const ref = useRef<T>(null);
  const allowClick = useRef(false);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const onMouseDown = useCallback((e: ReactMouseEvent) => {
    e.preventDefault();
    allowClick.current = true;
    isDragging.current = true;

    startX.current = e.pageX - (ref.current as HTMLElement).offsetLeft;
    scrollLeft.current = (ref.current as HTMLElement).scrollLeft;
  }, []);

  const onMouseMove = useCallback((e: ReactMouseEvent) => {
    if (!isDragging.current) return;
    e.preventDefault();
    allowClick.current = false;

    const x = e.pageX - startX.current;
    (ref.current as HTMLElement).scrollLeft = scrollLeft.current - x;
  }, []);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
    startX.current = 0;
    scrollLeft.current = 0;
  }, []);

  const handleClick = useCallback((e: MouseEvent) => {
    if (!allowClick.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    const el = ref.current;

    el?.addEventListener('click', handleClick);

    return () => {
      el?.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  return {
    ref,
    props: { onMouseDown, onMouseMove, onMouseUp },
  };
}
